import { useCallback, useEffect, useMemo, useState } from "react";

import classes from "./UserAdmin.module.scss";
import { fetchAdminGroupConfig, saveAdminGroupConfig } from "./ApiService";

const GroupAdmin = () => {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadGroups = useCallback(async ({ refresh } = {}) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminGroupConfig({ refresh });
      const items = Array.isArray(data.groups) ? data.groups : [];
      setGroups(items.map((item) => item.name));
      setSelected(
        items.filter((item) => item.selected).map((item) => item.name)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const selectedCount = selected.length;

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.localeCompare(b)),
    [groups]
  );

  const toggleGroup = (groupName) => {
    setSelected((current) =>
      current.includes(groupName)
        ? current.filter((name) => name !== groupName)
        : [...current, groupName]
    );
    setNotice("");
  };

  const saveGroups = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await saveAdminGroupConfig(selected);
      const saved = result.groups || selected;
      setSelected(saved);
      setNotice(
        saved.length
          ? `${saved.length} security group${saved.length === 1 ? "" : "s"} available in User Management.`
          : "No security groups selected for User Management."
      );
      await loadGroups();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <h1 className={classes.title}>Security Groups</h1>
          <p className={classes.subtitle}>
            Choose which Tableau groups can be assigned to portal users. Dashboard
            access is controlled by Tableau project permissions.
          </p>
        </div>
        <div className={classes.headerActions}>
          <button
            type="button"
            className={classes.secondaryBtn}
            onClick={() => loadGroups({ refresh: true })}
          >
            Refresh
          </button>
          <button
            type="button"
            className={classes.primaryBtn}
            onClick={saveGroups}
            disabled={saving || loading}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className={classes.error}>{error}</div>}
      {notice && <div className={classes.notice}>{notice}</div>}

      <div className={classes.tableWrapper}>
        {loading ? (
          <div className={classes.placeholder}>Loading Tableau groups…</div>
        ) : sortedGroups.length === 0 ? (
          <div className={classes.placeholder}>No Tableau groups found on the site.</div>
        ) : (
          <div className={classes.checkboxList} style={{ border: "none", borderRadius: 0 }}>
            <p className={classes.muted} style={{ margin: "0 0 8px" }}>
              {selectedCount} selected
            </p>
            {sortedGroups.map((group) => (
              <label key={group} className={classes.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selected.includes(group)}
                  onChange={() => toggleGroup(group)}
                />
                <span>{group}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupAdmin;
